import useSWR from 'swr';

import type { LicenseTier } from '@/api-models/balance';
import { fetchEndpointData } from '@/utils/fetch.client';

/** Mirrors the backend's `TeamRole` (serialised lowercase). */
export type TeamRole = 'owner' | 'admin' | 'member';

export interface Team {
  id: string;
  name: string;
  slug: string;
  role: TeamRole;
  /** The space's licence tier: free | starter | growth | scale. */
  license_tier: LicenseTier;
  is_personal: boolean;
  /** Default spend cap for new agent sessions in this space, in USD. `null` = uncapped. */
  default_session_budget_limit_usd: number | null;
  created_at: string;
  updated_at: string;
}

export interface TeamMember {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: TeamRole;
  created_at: string;
  last_seen_at: string;
  /** "active" or "deactivated". */
  status: string;
}

export interface TeamInvitation {
  id: string;
  team_id: string;
  email: string;
  role: TeamRole;
  status: string;
  expires_at: string;
  created_at: string;
}

/** A pending invitation addressed to the current user, with team + inviter context. */
export interface MyInvitation {
  id: string;
  team_id: string;
  team_name: string;
  team_license_tier: LicenseTier;
  role: TeamRole;
  invited_by_name: string;
  email: string;
  expires_at: string;
  created_at: string;
}

interface ListTeamsResponse {
  teams: Team[];
}

interface ListMyInvitationsResponse {
  invitations: MyInvitation[];
}

interface ListMembersResponse {
  members: TeamMember[];
}

interface ListInvitationsResponse {
  invitations: TeamInvitation[];
}

export function useTeams() {
  const { data, mutate, isLoading } = useSWR('/api/v1/teams', () =>
    fetchEndpointData<ListTeamsResponse>('/api/v1/teams'),
  );
  return { teams: data?.teams ?? [], mutate, isLoading };
}

export function useTeamMembers(teamId: string | null) {
  const { data, mutate, isLoading } = useSWR(teamId ? `/api/v1/teams/${teamId}/members` : null, () =>
    fetchEndpointData<ListMembersResponse>(`/api/v1/teams/${teamId}/members`),
  );
  return { members: data?.members ?? [], mutate, isLoading };
}

export function useTeamInvitations(teamId: string | null) {
  const { data, mutate, isLoading } = useSWR(teamId ? `/api/v1/teams/${teamId}/invitations` : null, () =>
    fetchEndpointData<ListInvitationsResponse>(`/api/v1/teams/${teamId}/invitations`),
  );
  return { invitations: data?.invitations ?? [], mutate, isLoading };
}

/** The current user's pending invitations across all teams (powers the Join tab). */
export function useMyInvitations() {
  const { data, mutate, isLoading } = useSWR('/api/v1/invitations', () =>
    fetchEndpointData<ListMyInvitationsResponse>('/api/v1/invitations'),
  );
  return { invitations: data?.invitations ?? [], mutate, isLoading };
}

export async function createTeam(body: { name: string }) {
  return fetchEndpointData<{ team: Team }>('/api/v1/teams', {
    method: 'POST',
    body,
  });
}

export async function updateTeam(teamId: string, body: { name: string }) {
  return fetchEndpointData<Team>(`/api/v1/teams/${teamId}`, {
    method: 'PUT',
    body,
  });
}

/**
 * Set the space's default spend cap for new agent sessions. `null` removes it.
 * Only sessions created afterwards are affected.
 */
export async function updateTeamSessionBudget(teamId: string, limitUsd: number | null) {
  return fetchEndpointData<Team>(`/api/v1/teams/${teamId}/session-budget`, {
    method: 'PUT',
    body: { limit_usd: limitUsd },
  });
}

export async function deleteTeam(teamId: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}`, { method: 'DELETE' });
}

export async function inviteMember(teamId: string, body: { email: string; role?: TeamRole }) {
  return fetchEndpointData<TeamInvitation>(`/api/v1/teams/${teamId}/invitations`, {
    method: 'POST',
    body,
  });
}

export async function removeMember(teamId: string, userId: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/members/${userId}`, {
    method: 'DELETE',
  });
}

export async function updateMemberRole(teamId: string, userId: string, role: TeamRole) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/members/${userId}/role`, {
    method: 'POST',
    body: { role },
  });
}

/** Activate or deactivate a member. `status` is "active" or "deactivated". */
export async function setMemberStatus(teamId: string, userId: string, status: string) {
  return fetchEndpointData<TeamMember>(`/api/v1/teams/${teamId}/members/${userId}/status`, {
    method: 'POST',
    body: { status },
  });
}

export async function revokeInvitation(teamId: string, invitationId: string) {
  return fetchEndpointData(`/api/v1/teams/${teamId}/invitations/${invitationId}`, {
    method: 'DELETE',
  });
}

export async function resendInvitation(teamId: string, invitationId: string) {
  return fetchEndpointData<TeamInvitation>(`/api/v1/teams/${teamId}/invitations/${invitationId}/resend`, {
    method: 'POST',
  });
}

export async function updateInvitationRole(teamId: string, invitationId: string, role: TeamRole) {
  return fetchEndpointData<TeamInvitation>(`/api/v1/teams/${teamId}/invitations/${invitationId}/role`, {
    method: 'POST',
    body: { role },
  });
}

export async function acceptInvitation(invitationId: string) {
  return fetchEndpointData(`/api/v1/invitations/${invitationId}/accept`, { method: 'POST' });
}

export async function declineInvitation(invitationId: string) {
  return fetchEndpointData(`/api/v1/invitations/${invitationId}/decline`, { method: 'POST' });
}
